import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import api from '@src/api/clients';

const splitTokens = (text = '') => text.split(/[\s,;]+/).map((v) => v.trim()).filter(Boolean);

function normalizeZip(raw = '') {
  const digits = String(raw).replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length >= 5) return digits.slice(0, 5);
  return digits.padStart(5, '0');
}

function uniqueZips(text = '') {
  return Array.from(new Set(splitTokens(text).map(normalizeZip).filter(Boolean)));
}

function ZipCodeUpdatesFlowPage() {
  const [clients, setClients] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [adAccounts, setAdAccounts] = useState([]);

  const [clientId, setClientId] = useState('');
  const [selectedVerticalIds, setSelectedVerticalIds] = useState([]);
  const [selectedTerritoryIds, setSelectedTerritoryIds] = useState([]);
  const [importMode, setImportMode] = useState('replace');
  const [applyAllVerticals, setApplyAllVerticals] = useState(false);
  const [zipText, setZipText] = useState('');

  const [adAccountId, setAdAccountId] = useState('');
  const [adSetIdsText, setAdSetIdsText] = useState('');
  const [includeFilteredOut, setIncludeFilteredOut] = useState(false);
  const [csvName, setCsvName] = useState('');

  const [previewResult, setPreviewResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [pushResult, setPushResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const zipList = useMemo(() => uniqueZips(zipText), [zipText]);

  useEffect(() => {
    let mounted = true;
    api
      .request({ url: 'dashboard/clients/?fields=id,name,facebook_accounts&limit=500', method: 'GET' })
      .then((res) => {
        if (!mounted) return;
        const rows = Array.isArray(res.data) ? res.data : res.data?.results || [];
        setClients(rows);
      })
      .catch(() => {
        if (!mounted) return;
        setError('Failed to load clients.');
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!clientId) {
      setVerticals([]);
      setTerritories([]);
      setAdAccounts([]);
      setAdAccountId('');
      return;
    }

    setError('');
    const client = clients.find((row) => String(row.id) === String(clientId));
    const accounts = client?.facebook_accounts || [];
    setAdAccounts(accounts);
    setAdAccountId(accounts[0]?.account_id || '');

    Promise.all([
      api.request({ url: `dashboard/verticals/?client_id=${clientId}`, method: 'GET' }),
      api.request({ url: `dashboard/client-territories/?client_id=${clientId}`, method: 'GET' }),
    ])
      .then(([verticalRes, territoryRes]) => {
        setVerticals(Array.isArray(verticalRes.data) ? verticalRes.data : verticalRes.data?.results || []);
        setTerritories(Array.isArray(territoryRes.data) ? territoryRes.data : territoryRes.data?.results || []);
      })
      .catch(() => setError('Failed to load client scope options.'));
  }, [clientId, clients]);

  const buildImportPayload = () => ({
    client_id: Number(clientId),
    rows: zipList.map((zip) => ({ zip })),
    import_mode: importMode,
    default_vertical_ids: selectedVerticalIds.map(Number),
    scope_vertical_ids: selectedVerticalIds.map(Number),
    scope_territory_ids: selectedTerritoryIds.map(Number),
    apply_to_all_verticals: applyAllVerticals,
  });

  const validate = () => {
    if (!clientId) return 'Select a client.';
    if (!selectedVerticalIds.length) return 'Select at least one vertical.';
    if (!zipList.length) return 'Provide ZIPs.';
    return '';
  };

  const handlePreview = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await api.request({
        url: 'dashboard/client-zips/preview-import/',
        method: 'POST',
        payload: buildImportPayload(),
      });
      setPreviewResult(res.data || null);
    } catch {
      setError('Preview failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await api.request({
        url: 'dashboard/client-zips/apply-import/',
        method: 'POST',
        payload: buildImportPayload(),
      });
      setImportResult(res.data || null);
    } catch {
      setError('Apply import failed.');
    } finally {
      setLoading(false);
    }
  };

  const handlePush = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    const adSetIds = splitTokens(adSetIdsText);
    if (!adAccountId) {
      setError('Select an ad account.');
      return;
    }
    if (!adSetIds.length) {
      setError('Provide at least one ad set ID.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await api.request({
        url: 'dashboard/facebook/zip-targeting/push/',
        method: 'POST',
        payload: {
          client_id: Number(clientId),
          ad_account_id: adAccountId,
          ad_set_ids: adSetIds,
          vertical_ids: selectedVerticalIds.map(Number),
          territory_ids: selectedTerritoryIds.map(Number),
          include_filtered_out: includeFilteredOut,
          source_type: 'zip_update_flow_react',
          csv_name: csvName,
        },
      });
      setPushResult(res.data || null);
    } catch {
      setError('Facebook push failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={2.5}>
      <div>
        <Typography variant="h4">ZIP Code Update Flow</Typography>
        <Typography color="text.secondary" variant="body2">
          Process ZIP update requests from intake through import and Facebook targeting push.
        </Typography>
      </div>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={1.5}>
                <Typography variant="h6">1) Scope</Typography>
                <TextField
                  fullWidth
                  label="Client"
                  onChange={(e) => {
                    setClientId(e.target.value);
                    setSelectedVerticalIds([]);
                    setSelectedTerritoryIds([]);
                  }}
                  select
                  size="small"
                  value={clientId}
                >
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={String(client.id)}>
                      {client.name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  fullWidth
                  label="Verticals"
                  onChange={(e) => setSelectedVerticalIds(e.target.value)}
                  select
                  SelectProps={{ multiple: true }}
                  size="small"
                  value={selectedVerticalIds}
                >
                  {verticals.map((row) => (
                    <MenuItem key={row.id} value={String(row.id)}>
                      {row.vertical}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  fullWidth
                  label="Territories (optional)"
                  onChange={(e) => setSelectedTerritoryIds(e.target.value)}
                  select
                  SelectProps={{ multiple: true }}
                  size="small"
                  value={selectedTerritoryIds}
                >
                  {territories.map((row) => (
                    <MenuItem key={row.id} value={String(row.id)}>
                      {row.name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  fullWidth
                  label="Import Mode"
                  onChange={(e) => setImportMode(e.target.value)}
                  select
                  size="small"
                  value={importMode}
                >
                  <MenuItem value="replace">replace</MenuItem>
                  <MenuItem value="add">add</MenuItem>
                </TextField>

                <FormControlLabel
                  control={<Checkbox checked={applyAllVerticals} onChange={(e) => setApplyAllVerticals(e.target.checked)} />}
                  label="Apply to all verticals"
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={1.5}>
                <Typography variant="h6">2) ZIP Intake</Typography>
                <TextField
                  fullWidth
                  helperText={`${zipList.length} unique ZIPs detected`}
                  label="Paste ZIPs"
                  minRows={10}
                  multiline
                  onChange={(e) => setZipText(e.target.value)}
                  placeholder={'10001, 10002\n30301\n94105'}
                  value={zipText}
                />
                <Button
                  onClick={() => setZipText(zipList.join('\n'))}
                  variant="outlined"
                >
                  Normalize + Dedupe
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card variant="outlined">
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button disabled={loading} onClick={handlePreview} variant="outlined">Preview Import</Button>
            <Button disabled={loading} onClick={handleApply} variant="contained">Apply Import</Button>
          </Stack>
          {previewResult ? (
            <Box sx={{ mt: 1.5 }}>
              <Alert severity="info">
                Preview: create {previewResult?.summary?.to_create || 0}, update {previewResult?.summary?.to_update || 0}, deactivate {previewResult?.summary?.to_deactivate || 0}
              </Alert>
            </Box>
          ) : null}
          {importResult ? (
            <Box sx={{ mt: 1.5 }}>
              <Alert severity="success">
                Applied: created {importResult?.summary?.created || 0}, updated {importResult?.summary?.updated || 0}, deactivated {importResult?.summary?.deactivated || 0}
              </Alert>
            </Box>
          ) : null}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant="h6">3) Push to Facebook</Typography>

            <TextField
              fullWidth
              label="Ad Account"
              onChange={(e) => setAdAccountId(e.target.value)}
              select
              size="small"
              value={adAccountId}
            >
              {adAccounts.map((acct) => (
                <MenuItem key={acct.account_id} value={acct.account_id}>
                  {acct.account_name} ({acct.account_id})
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              label="Ad Set IDs (space/newline/comma separated)"
              minRows={3}
              multiline
              onChange={(e) => setAdSetIdsText(e.target.value)}
              placeholder={'1234567890\n9876543210'}
              value={adSetIdsText}
            />

            <TextField
              fullWidth
              label="CSV Name (optional)"
              onChange={(e) => setCsvName(e.target.value)}
              size="small"
              value={csvName}
            />

            <FormControlLabel
              control={<Checkbox checked={includeFilteredOut} onChange={(e) => setIncludeFilteredOut(e.target.checked)} />}
              label="Include filtered out ZIPs"
            />

            <div>
              <Button disabled={loading} onClick={handlePush} variant="contained">
                Push ZIP Targeting
              </Button>
            </div>

            {pushResult ? (
              <Alert severity="success">
                Push complete: matched {pushResult.zip_count || 0}, pushed {pushResult.pushed_zip_count || 0}, ad sets {(pushResult.results || []).length}
              </Alert>
            ) : null}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}

export default ZipCodeUpdatesFlowPage;
